import { http } from '@/api'

export interface Permission {
  id: number
  name: string
  code: string
  description?: string
  module: string
  controller?: string
  action?: string
  route_name?: string
  parent_id: number
  level: number
  type: number // 1: 菜单, 2: 操作
  type_text: string
  sort_order: number
  status: number // 0: 禁用, 1: 启用
  status_text: string
  created_at: string
  updated_at: string
  children?: Permission[]
}

export interface PermissionStatistics {
  total_permissions: number
  enabled_permissions: number
  menu_permissions: number
  operation_permissions: number
  modules: string[]
  permission_by_module: Record<string, number>
}

export interface PermissionListParams {
  page?: number
  per_page?: number
  name?: string
  code?: string
  module?: string
  type?: number // 1: 菜单, 2: 操作
  status?: number // 0: 禁用, 1: 启用
  parent_id?: number
  all?: boolean
  tree?: boolean
}

export interface CreatePermissionData {
  name: string
  code: string
  description?: string
  module: string
  controller?: string
  action?: string
  route_name?: string
  parent_id?: number
  type: number // 1: 菜单, 2: 操作
  sort_order?: number
  status?: number // 0: 禁用, 1: 启用
}

export interface UpdatePermissionData extends Partial<CreatePermissionData> {}

export interface PermissionOption {
  value: number
  label: string
  code: string
  level: number
  type: number
  children?: PermissionOption[]
}

export interface BatchStatusData {
  ids: number[]
  status: boolean // true: 启用, false: 禁用
}

export interface AdminPermissionData {
  admin: any
  roles: any[]
  permissions: Permission[]
  permission_tree: Permission[]
}

export interface RolePermissionData {
  role: any
  permissions: Permission[]
  permission_tree: Permission[]
}

export interface AssignRoleData {
  admin_id: number
  role_id: number
}

export interface RemoveRoleData {
  admin_id: number
  role_id: number
}

export interface AssignPermissionData {
  role_id: number
  permission_ids: number[]
}

// 权限管理API
export const permissionApi = {
  // 获取权限统计信息
  getStatistics(): Promise<ApiResponse<PermissionStatistics>> {
    return http.get('/admin/permissions/statistics')
  },

  // 获取权限列表
  getList(params?: PermissionListParams): Promise<ApiResponse<Permission[] | PaginatedData<Permission>>> {
    return http.get('/admin/permissions', { params })
  },

  // 获取权限详情
  getDetail(id: number): Promise<ApiResponse<{
    permission: Permission
    parent?: Permission
    children_count: number
    full_path: string
  }>> {
    return http.get(`/admin/permissions/${id}`)
  },

  // 创建权限
  create(data: CreatePermissionData): Promise<ApiResponse<Permission>> {
    return http.post('/admin/permissions', data)
  },

  // 更新权限
  update(id: number, data: UpdatePermissionData): Promise<ApiResponse<Permission>> {
    return http.put(`/admin/permissions/${id}`, data)
  },

  // 删除权限
  delete(id: number): Promise<ApiResponse<null>> {
    return http.delete(`/admin/permissions/${id}`)
  },

  // 获取权限选项（用于下拉选择）
  getOptions(params?: {
    type?: number
    module?: string
    tree?: boolean
  }): Promise<ApiResponse<PermissionOption[]>> {
    return http.get('/admin/permissions/options', { params })
  },

  // 批量更新权限状态
  batchUpdateStatus(data: BatchStatusData): Promise<ApiResponse<{ affected_count: number }>> {
    return http.patch('/admin/permissions/batch/status', data)
  },

  // 获取管理员权限信息
  getAdminPermissions(adminId?: number): Promise<ApiResponse<AdminPermissionData>> {
    const url = adminId ? `/admin/permissions/admin/${adminId}` : '/admin/permissions/admin'
    return http.get(url)
  },

  // 为管理员分配角色
  assignRole(data: AssignRoleData): Promise<ApiResponse<null>> {
    return http.post('/admin/permissions/assign-role', data)
  },

  // 移除管理员角色
  removeRole(data: RemoveRoleData): Promise<ApiResponse<null>> {
    return http.request({
      method: 'DELETE',
      url: '/admin/permissions/remove-role',
      data,
    })
  },

  // 获取角色权限
  getRolePermissions(roleId: number): Promise<ApiResponse<RolePermissionData>> {
    return http.get(`/admin/permissions/role/${roleId}`)
  },

  // 为角色分配权限
  assignPermission(data: AssignPermissionData): Promise<ApiResponse<null>> {
    return http.post('/admin/permissions/assign-permission', data)
  },

  // 批量分配角色
  batchAssignRoles(data: {
    admin_id: number
    role_ids: number[]
  }): Promise<ApiResponse<{
    success_count: number
    errors: string[]
  }>> {
    return http.post('/admin/permissions/batch-assign-roles', data)
  },
}

// 权限工具函数
export const permissionUtils = {
  // 构建权限树
  buildTree(permissions: Permission[], parentId = 0): Permission[] {
    const tree: Permission[] = []

    permissions.forEach((permission) => {
      if (permission.parent_id === parentId) {
        const children = this.buildTree(permissions, permission.id)
        if (children.length > 0) {
          permission.children = children
        }
        tree.push(permission)
      }
    })

    return tree
  },

  // 获取所有权限ID（包括子权限）
  getAllPermissionIds(permissions: Permission[]): number[] {
    const ids: number[] = []

    permissions.forEach((permission) => {
      ids.push(permission.id)
      if (permission.children && permission.children.length > 0) {
        ids.push(...this.getAllPermissionIds(permission.children))
      }
    })

    return ids
  },

  // 获取权限类型文本
  getTypeText(type: number): string {
    const typeMap: Record<number, string> = {
      1: '菜单',
      2: '操作',
    }
    return typeMap[type] || '未知'
  },

  // 获取权限状态文本
  getStatusText(status: number): string {
    const statusMap: Record<number, string> = {
      0: '禁用',
      1: '启用',
    }
    return statusMap[status] || '未知'
  },

  // 获取权限类型颜色
  getTypeColor(type: number): 'primary' | 'success' | 'warning' | 'info' | 'danger' {
    const colorMap: Record<number, 'primary' | 'success' | 'warning' | 'info' | 'danger'> = {
      1: 'primary', // 菜单
      2: 'warning', // 操作
    }
    return colorMap[type] || 'info'
  },

  // 过滤权限（根据搜索条件）
  filterPermissions(permissions: Permission[], keyword: string): Permission[] {
    if (!keyword) {
      return permissions
    }

    return permissions.filter((permission) => {
      const matchSelf = permission.name.includes(keyword)
        || permission.code.includes(keyword)
        || (permission.description && permission.description.includes(keyword))

      const matchChildren = permission.children && this.filterPermissions(permission.children, keyword).length > 0

      return matchSelf || matchChildren
    })
  },
}
